// Updated ProcessDischargeModal.jsx (Fixed: Removed invalid <style jsx> tag, added global CSS note)
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { toast } from "react-hot-toast";
import Select from "react-select/async"; // For typeahead
import { format, differenceInDays, parseISO } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faCheck,
  faTimes,
  faPrint,
  faDownload,
  faClock,
  faSpinner,
  faFileMedical,
  faSyncAlt,
  faHospital,
  faUser,
  faCalendar,
  faInfoCircle,
  faExclamationTriangle,
  faUpload,
  faFilePdf,
  faEye,
  faEdit,
  faSave,
  faArrowLeft,
  faBed,
  faShieldAlt,
  faStethoscope,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config"; // Your config

// Schema remains the same
const schema = yup.object({
  admission_date: yup.string().required("Required"),
  discharge_date: yup
    .string()
    .required("Required")
    .test("after-admission", "Must be after admission", function (value) {
      const ad = this.parent.admission_date;
      return (
        !ad || !value || differenceInDays(parseISO(value), parseISO(ad)) > 0
      );
    }),
  soa_received: yup.string().required("Required"),
  initial_icd: yup.string().nullable(),
  final_diagnosis: yup.string().required("Required"),
  rnb_rows: yup
    .array()
    .of(
      yup.object({
        type: yup.string().required(),
        room_no: yup.string(),
        rate: yup.number().min(0),
        days: yup.number().min(1).required(),
        amt: yup.number().min(0),
        cov: yup.number().min(0),
      })
    )
    .min(1),
  phys_rows: yup
    .array()
    .of(
      yup.object({
        phys_name: yup.string().required(),
        role: yup.string().required(),
        amt: yup.number().min(0).required(),
        ph: yup.number().min(0),
      })
    )
    .min(1),
  surgical_rows: yup
    .array()
    .of(
      yup.object({
        procedure: yup.string().required(),
        amt: yup.number().min(0).required(),
        ph: yup.number().min(0),
      })
    )
    .min(0),
  misc_rows: yup
    .array()
    .of(
      yup.object({
        description: yup.string().required(),
        amt: yup.number().min(0).required(),
        ph: yup.number().min(0),
      })
    )
    .min(0),
  corporate_guarantee_tag: yup.boolean(),
  provider_guarantee_tag: yup.boolean(),
  total_rnb_ph: yup.number().min(0),
  delay_responsible: yup.string().when("showDelay", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  delay_reason: yup.string().when("showDelay", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  delay_other: yup.string().when("delay_reason", {
    is: "Other",
    then: (schema) => schema.required("Required"),
  }),
  guaranteed_amount: yup.number().min(0).when("corporate_guarantee_tag", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  corp_approved_by: yup.string().when("corporate_guarantee_tag", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  amap_approved_by: yup.string().when("provider_guarantee_tag", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  remarks: yup.string(),
  claim_remarks: yup.string(),
  proof_guarantee: yup.mixed().when("corporate_guarantee_tag", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  soa_files: yup.mixed().required("Required"),
  ph_file: yup.boolean(),
  ph_comp: yup.boolean(),
  vip_tag: yup.boolean(), // Added for VIP checkbox
});

const ProcessDischargeModal = ({ visible, transNo, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState("patient-details"); // Default to Patient Details
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vipChecked, setVipChecked] = useState(false);
  const [showDelay, setShowDelay] = useState(false);
  const [isMaternity, setIsMaternity] = useState(false);
  const [loaVisible, setLoaVisible] = useState(false);
  const [loaNo, setLoaNo] = useState(0);

  // Form hook
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    control,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      admission_date: "",
      discharge_date: "",
      soa_received: "",
      initial_icd: null,
      final_diagnosis: "",
      rnb_rows: [
        {
          type: "",
          room_no: "",
          rate: 0,
          days: 0,
          amt: 0,
          cov: 0,
        },
      ],
      phys_rows: [
        { phys_name: "", role: "", amt: 0, ph: 0 },
      ],
      surgical_rows: [],
      misc_rows: [],
      corporate_guarantee_tag: false,
      provider_guarantee_tag: false,
      total_rnb_ph: 0,
      delay_responsible: "",
      delay_reason: "",
      delay_other: "",
      guaranteed_amount: 0,
      corp_approved_by: "",
      amap_approved_by: "",
      remarks: "",
      claim_remarks: "",
      proof_guarantee: null,
      soa_files: null,
      ph_file: false,
      ph_comp: false,
      vip_tag: false, // Added
    },
  });

  const { fields: rnbFields, append: appendRnb, remove: removeRnb } = useFieldArray({
    control,
    name: "rnb_rows",
  });

  const { fields: physFields, append: appendPhys, remove: removePhys } = useFieldArray({
    control,
    name: "phys_rows",
  });

  const { fields: surgicalFields, append: appendSurgical, remove: removeSurgical } = useFieldArray({
    control,
    name: "surgical_rows",
  });

  const { fields: miscFields, append: appendMisc, remove: removeMisc } = useFieldArray({
    control,
    name: "misc_rows",
  });

  // Fetch details on mount if visible
  useEffect(() => {
    if (visible && transNo) {
      setLoading(true);
      // Simulate API call - replace with actual axios.get(`${BASE_URL}/discharge/${transNo}`)
      setTimeout(() => {
        setDetails({
          // Mock data - replace with real response
          hospital_name: "Sample Hospital",
          member_name: "John Doe",
          plan: { copay_company: "10%", copay_member: "20%" },
          rnb_benefit: { rnb_basic: 50000, rnb_major: 100000, rnb_category: "Private", rnb_amount: 75000 },
          anes_benefit: { anes_basic: "As Charged", anes_major: "As Charged" },
          provider_prof_fees: [{ ip_rate_anes: 5000 }],
        });
        setLoading(false);
        // Set form values
        setValue("admission_date", "2025-11-01");
        setValue("discharge_date", "2025-11-10");
        setValue("soa_received", format(new Date(), "yyyy-MM-dd"));
        setValue("final_diagnosis", "Sample Diagnosis");
      }, 1000);
    }
  }, [visible, transNo, setValue]);

  // Tab switch handler
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  // Form submit handler (placeholder)
  const onSubmit = (data) => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      onSuccess(`LOA-${transNo}-${Date.now()}`);
      setLoading(false);
    }, 2000);
  };

  // Close handler
  const handleClose = () => {
    reset();
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-300 sm:max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faFileMedical} className="text-2xl" />
            <div>
              <h3 className="text-xl font-bold">Process Discharge</h3>
              <p className="text-sm opacity-90">Transaction # {transNo}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-full p-2 text-white hover:bg-white hover:bg-opacity-20 transition-all"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FontAwesomeIcon icon={faSpinner} spin className="h-12 w-12 text-indigo-600" />
              <p className="mt-4 text-gray-600">Loading discharge details...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => handleTabSwitch("patient-details")}
                    className={`border-b-2 px-1 py-4 text-sm font-medium transition-all ${
                      activeTab === "patient-details"
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Patient Details
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTabSwitch("confinement")}
                    className={`border-b-2 px-1 py-4 text-sm font-medium transition-all ${
                      activeTab === "confinement"
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    Confinement Information
                  </button>
                </nav>
              </div>

              <div className="tab-content space-y-6">
                {/* Patient Details Tab */}
                {activeTab === "patient-details" && (
                  <div className="animate-fade-in">
                    {/* Hospital Info */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div className="space-y-2 rounded-lg bg-gray-50 p-4">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                          <FontAwesomeIcon icon={faHospital} className="text-indigo-500" />
                          <span>Hospital Name</span>
                        </label>
                        <input
                          type="text"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                          value={details?.hospital_name || ""}
                          disabled
                        />
                      </div>

                      {/* Member Name */}
                      <div className="space-y-2 rounded-lg bg-gray-50 p-4">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                          <FontAwesomeIcon icon={faUser} className="text-indigo-500" />
                          <span>Member Name</span>
                        </label>
                        <input
                          type="text"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                          value={details?.member_name || ""}
                          disabled
                        />
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                          <FontAwesomeIcon icon={faCalendar} className="text-indigo-500" />
                          <span>Admission Date</span>
                        </label>
                        <input
                          type="date"
                          {...register("admission_date")}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        {errors.admission_date && (
                          <p className="text-xs text-red-500">{errors.admission_date.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                          <FontAwesomeIcon icon={faCalendar} className="text-indigo-500" />
                          <span>Discharge Date</span>
                        </label>
                        <input
                          type="date"
                          {...register("discharge_date")}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        {errors.discharge_date && (
                          <p className="text-xs text-red-500">{errors.discharge_date.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">No. of Days Confined</label>
                        <input
                          type="number"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-100"
                          disabled
                        />
                      </div>
                    </div>

                    {/* SOA Received */}
                    <div className="space-y-2 rounded-lg bg-blue-50 p-4">
                      <label className="flex items-center space-x-2 text-sm font-semibold text-blue-800">
                        <FontAwesomeIcon icon={faUpload} className="text-blue-500" />
                        <span>Date & Time Final SOA Received *</span>
                      </label>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                        <input
                          type="date"
                          {...register("soa_received")}
                          className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <div className="flex space-x-1">
                          <input
                            type="number"
                            placeholder="HH"
                            min="1"
                            max="12"
                            className="w-full rounded-md border border-gray-300 px-2 py-2 text-center text-sm"
                          />
                          <span className="flex items-center px-2 text-gray-500">:</span>
                          <input
                            type="number"
                            placeholder="MM"
                            min="0"
                            max="59"
                            className="w-full rounded-md border border-gray-300 px-2 py-2 text-center text-sm"
                          />
                        </div>
                        <select className="rounded-md border border-gray-300 px-3 py-2 text-sm">
                          <option>AM</option>
                          <option>PM</option>
                        </select>
                      </div>
                      {errors.soa_received && (
                        <p className="text-xs text-red-500">{errors.soa_received.message}</p>
                      )}
                    </div>

                    {/* Delay Section - Conditional */}
                    {showDelay && (
                      <div className="space-y-2 rounded-lg border-2 border-red-200 bg-red-50 p-4">
                        <label className="flex items-center space-x-2 text-sm font-semibold text-red-800">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />
                          <span>Delay Information *</span>
                        </label>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                          <select
                            {...register("delay_responsible")}
                            className="rounded-md border border-red-300 px-3 py-2 text-sm focus:border-red-500"
                          >
                            <option value="">Select Responsible</option>
                            <option value="Processor">Processor</option>
                            <option value="System">System</option>
                            <option value="Provider">Provider</option>
                          </select>
                          <select {...register("delay_reason")} className="rounded-md border border-red-300 px-3 py-2 text-sm">
                            <option value="">Select Reason</option>
                            <option value="Administrative">Administrative</option>
                            <option value="Technical">Technical</option>
                            <option value="Other">Other</option>
                          </select>
                          {watch("delay_reason") === "Other" && (
                            <input
                              {...register("delay_other")}
                              placeholder="Specify other reason"
                              className="rounded-md border border-red-300 px-3 py-2 text-sm"
                            />
                          )}
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowDelay(!showDelay)}
                      className="text-sm text-blue-600 underline hover:text-blue-800"
                    >
                      {showDelay ? "Hide Delay Details" : "Add Delay Information"}
                    </button>

                    {/* VIP Checkbox */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="vip_tag"
                        {...register("vip_tag")}
                        onChange={(e) => setVipChecked(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="vip_tag" className="text-sm font-medium text-gray-700">
                        VIP Tag
                      </label>
                    </div>

                    {/* Benefits Panels */}
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {/* R&B Benefit */}
                      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm">
                        <h5 className="flex items-center space-x-2 text-sm font-semibold text-gray-900">
                          <FontAwesomeIcon icon={faBed} className="text-indigo-500" />
                          <span>Room & Board Benefit</span>
                        </h5>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                          <div>
                            <label className="text-xs text-gray-600">Basic Limit</label>
                            <input
                              className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                              value={formatMoney(details?.rnb_benefit?.rnb_basic ?? 0)}
                              disabled
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Major Limit</label>
                            <input
                              className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                              value={formatMoney(details?.rnb_benefit?.rnb_major ?? 0)}
                              disabled
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Category</label>
                            <input
                              className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                              value={details?.rnb_benefit?.rnb_category || ""}
                              disabled
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Amount</label>
                            <input
                              className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                              value={formatMoney(details?.rnb_benefit?.rnb_amount ?? 0)}
                              disabled
                            />
                          </div>
                        </div>
                      </div>

                      {/* CoPay */}
                      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm">
                        <h5 className="flex items-center space-x-2 text-sm font-semibold text-gray-900">
                          <FontAwesomeIcon icon={faShieldAlt} className="text-green-500" />
                          <span>CoPay</span>
                        </h5>
                        <div className="grid grid-cols-1 gap-2">
                          <div>
                            <label className="text-xs text-gray-600">Copay c/o Company (%)</label>
                            <input
                              className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                              value={details?.plan?.copay_company || "0%"}
                              disabled
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Copay c/o Member (%)</label>
                            <input
                              className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                              value={details?.plan?.copay_member || "0%"}
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Anesthesiologist */}
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm">
                      <h5 className="flex items-center space-x-2 text-sm font-semibold text-gray-900">
                        <FontAwesomeIcon icon={faStethoscope} className="text-purple-500" />
                        <span>Anesthesiologist Benefit Details</span>
                      </h5>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                        <div>
                          <label className="text-xs text-gray-600">Basic Benefit</label>
                          <input
                            className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                            value={details?.anes_benefit?.anes_basic ?? ""}
                            disabled
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Major Benefit</label>
                          <input
                            className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                            value={details?.anes_benefit?.anes_major ?? ""}
                            disabled
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Provider IP PF Rate</label>
                          <input
                            className="w-full rounded-md border border-gray-300 px-3 py-1 text-sm disabled:bg-gray-100"
                            value={formatMoney(details?.provider_prof_fees[0]?.ip_rate_anes || 0)}
                            disabled
                          />
                        </div>
                      </div>
                    </div>

                    {/* Final Diagnosis */}
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-indigo-500" />
                        <span>Final Diagnosis *</span>
                      </label>
                      <textarea
                        {...register("final_diagnosis")}
                        rows={3}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        placeholder="Enter final diagnosis..."
                      />
                      {errors.final_diagnosis && (
                        <p className="text-xs text-red-500">{errors.final_diagnosis.message}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Confinement Information Tab - Placeholder with modern UI */}
                {activeTab === "confinement" && (
                  <div className="animate-fade-in space-y-6">
                    <div className="text-center py-12">
                      <FontAwesomeIcon icon={faHospital} className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-lg font-medium text-gray-900">Confinement Information</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Detailed confinement data will be available here soon. Currently under development.
                      </p>
                      <button
                        type="button"
                        onClick={() => toast.info("Coming soon!")}
                        className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                      >
                        Preview Sample
                      </button>
                    </div>

                    {/* Example Interactive Section - Add RNB/Phys rows if needed */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h5 className="mb-3 text-sm font-semibold text-gray-900">Sample Room & Board Rows</h5>
                      {rnbFields.map((field, index) => (
                        <div key={field.id} className="flex items-center space-x-2 rounded-md bg-white p-2 shadow-sm">
                          <input
                            {...register(`rnb_rows.${index}.room_no`)}
                            placeholder="Room No"
                            className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
                          />
                          <input
                            type="number"
                            {...register(`rnb_rows.${index}.days`)}
                            placeholder="Days"
                            className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeRnb(index)}
                            className="rounded p-1 text-red-500 hover:bg-red-100"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => appendRnb({ type: "", room_no: "", rate: 0, days: 1, amt: 0, cov: 0 })}
                        className="mt-2 flex items-center space-x-2 rounded-md bg-green-50 px-3 py-1 text-sm text-green-700 hover:bg-green-100"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Add Row</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
            className="flex items-center space-x-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faCheck} spin={loading} />
            <span>{loading ? "Processing..." : "Process Discharge"}</span>
          </button>
        </div>
      </div>

      {/* Note: For fade-in animation, add this to your global CSS file (e.g., index.css): */}
      {/* 
      .animate-fade-in {
        animation: fadeIn 0.3s ease-in-out;
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      */}
    </div>
  );
};

// Helper function for money formatting
const formatMoney = (value) => {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
  }).format(value || 0);
};

export default ProcessDischargeModal;